import { Merge } from "types/_utils.types";
import { T_MongoId } from "../types/_utils.types";
import { CreditorMatchSync, LoanSchemaDto } from "./loan.dto";
import { ApplicationDto } from "./application.dto";
import { CreditorDto } from "./creditor.dto";
// import { LoanStatus } from "./app-creditor-map.dto";

export type ColumnTemplateRef = {
  _id: string;
  name: string;
  slug: string;
  triggers: string[];
};

export type ColumnDto = {
  id: string; // Internal custom ID to track cards even if name changes
  name: string;
  group?: string;
  position: number;
  meta?: Record<string, any>;
  action?: string;
  disabled: boolean;
  templates?: ColumnTemplateRef[];
};

export type GroupDto = {
  name: string;
  position: number;
  meta?: Record<string, any>;
};

export type CreateColumnDto = Omit<ColumnDto, "id">;

export enum BoardResource {
  APPLICATION = "Application",
  LEAD = "Lead",
  LOAN = "Loan",
  CREDITOR = "Creditor",
}

// export interface CardDto {
//   _id: string;
//   board_id: string;
//   column_id: string;
//   title: string;
//   description?: string;
//   tags: string[];
//   type: string;
//   item: any;
//   reference_id: string;
//   created_by: string;
// }

enum BoardType {
  APPLICATION = "application",
}

enum ItemType {
  APPLICATION = "application",
}

// export interface BoardCardItem {
//   _id: string;
//   app_key: string;
//   archived: boolean;
//   approval_requested: boolean;
//   last_step: string;
//   enrolled_debt: number;
//   total_banks: number;
//   total_lawsuits: number;
//   total_loans: number;
// }

export interface ClientMeta {
  client_name: string;
  business_name: string;
  email: string;
  phone: string;
  address: string;
}

export interface AffiliatePhone {
  verified: boolean;
  code: string;
  number: string;
}

export interface Affiliate {
  _id: string;
  slug: string;
  name: string;
  email: string;
  profile_image: string;
  phone: AffiliatePhone;
}

export interface OrganisationColor {
  primary: string;
  secondary: string;
}

export interface OrganisationMeta {
  color: OrganisationColor;
  logo: string;
  website: string;
  favicon: string;
  title: string;
  company_name: string;
  tagline: string;
}

export interface Organisation {
  _id: string;
  code: string;
  meta: OrganisationMeta;
}

export interface ApplicationItem {
  _id: string;
  app_key: string;
  meta: ClientMeta;
  affiliate: Affiliate;
  organisation: Organisation;
  archived: boolean;
  approval_requested: boolean;
  createdAt: string; // ISO string
  last_step: string;
  enrolled_debt: number;
  total_banks: number;
  total_lawsuits: number;
  total_loans: number;
  source_uri?: string;
}

export type CardType = BoardResource;

export interface ApplicationCardDto {
  _id: string;
  type: "application";
  app_key: string;

  meta: {
    client_name?: string;
    business_name?: string;
    email?: string;
    phone?: string;
    address?: string;
  };

  affiliate?: {
    name?: string;
  };

  organisation?: {
    meta?: {
      company_name?: string;
    };
  };

  enrolled_debt?: number;
  total_banks?: number;
  total_lawsuits?: number;
  total_loans?: number;
  last_step?: string;
  source_uri?: string;
  createdAt: string;
}

export interface CreditorCardDto {
  _id: string;
  type: "Creditor";

  name: string;
  slug: string;

  allowed_domains: string[];
  allowed_emails: string[];
  allowed_phones: string[];
  alternate_names: string[];

  inDraft: boolean;
  createdAt: string;
  updatedAt: string;
}

enum Frequency {
  WEEKLY = "weekly",
  MONTHLY = "monthly",
  DAILY = "daily",
  YEARLY = "yearly",
}

enum Priority {
  NONE = "None",
  NORMAL = "Normal",
  HIGH = "High",
}

export type LoanCardDto = LoanSchemaDto & {
  application: ApplicationDto;
  creditor?: CreditorDto;
  certainty?: CreditorMatchSync;
  status: string;
};

export interface CardDto {
  _id: string;
  board_id: string;
  column_id: string;
  title: string;
  description: string;
  tags: string[];
  type: CardType;
  reference_id: string;
  created_by: string;
  createdAt: string;
  updatedAt: string;
  item: ApplicationCardDto | CreditorCardDto | LoanCardDto;
}

export type BoardDto = {
  _id: string;
  name: string;
  resource: BoardResource;
  application?: T_MongoId;
  createdBy: T_MongoId;
  columns: ColumnDto[];
  groups?: GroupDto[];
  scope?: string;
  organisation?: { _id: string; code: string } | string;
  isSyncing: boolean;
  lastSyncSession?: Date[];
  cooldownTill: Date | null;
  syncCooldown: number;
  totalCards: number;
  createdAt: string;
};

export type CreateBoardDto = Merge<
  Pick<BoardDto, "name" | "resource" | "groups" | "scope">,
  {
    columns?: CreateColumnDto[];
    appKey?: string;
    orgCode: string;
  }
>;

export type T_EmailType = "regular" | "system_fault";
